import { useAuth } from '../lib/contextoAuth';
import {
  describirAccion, describirSobreQue, describirDesde, describirDispositivo, describirCampo, aplanar,
} from '../experiencia/auditoria';
import { useCallback, useEffect, useState } from 'react';
import { FileSearch } from 'lucide-react';
import { api, traerTodo } from '../api';
import { useListaUrl, consultaLista } from '../lib/urlEstado';
import ListaEstandar from '../ui/ListaEstandar';
import Paginador from '../ui/Paginador';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import NotaExplicativa from '../ui/NotaExplicativa';
import { Buscador, SelectFiltro, RangoFechas } from '../ui/Filtros';
import Modal from '../ui/Modal';

const POR_PAGINA = 100;

// Las columnas de exportación llevan MÁS que la tabla: un auditor externo
// pide el antes y el después completos, y el navegador no es el sitio para
// leer un JSON de veinte campos.
const COLS_EXPORT = [
  { etiqueta: 'Fecha', tipo: 'fecha_hora', valor: (r) => r.fecha },
  { etiqueta: 'Usuario', valor: (r) => r.usuario || '' },
  { etiqueta: 'Correo', valor: (r) => r.email || '' },
  { etiqueta: 'Acción', valor: (r) => r.accion },
  { etiqueta: 'Entidad', valor: (r) => r.entidad },
  { etiqueta: 'ID entidad', valor: (r) => r.id_entidad ?? '' },
  { etiqueta: 'IP de origen', valor: (r) => r.ip_origen || '' },
  { etiqueta: 'Dispositivo', valor: (r) => r.user_agent || '' },
  { etiqueta: 'Antes', valor: (r) => (r.datos_antes ? JSON.stringify(r.datos_antes) : '') },
  { etiqueta: 'Después', valor: (r) => (r.datos_despues ? JSON.stringify(r.datos_despues) : '') },
];

export default function Auditoria() {
  // Un filtro de auditoría es exactamente lo que se manda por mensaje:
  // «mira lo que hizo Fulano el martes». Antes había que dictar los pasos.
  const { filtros, pagina, porPagina, desplazamiento, fijarFiltro, fijarPagina } =
    useListaUrl(
      { accion: '', id_usuario: '', buscar: '', desde: '', hasta: '' },
      { porPagina: POR_PAGINA },
    );

  const [registros, setRegistros] = useState([]);
  const [total, setTotal] = useState(null);
  const [acciones, setAcciones] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [detalle, setDetalle] = useState(null);

  useEffect(() => {
    let vigente = true;
    api.get('/auditoria/acciones').then((a) => vigente && setAcciones(a)).catch(() => {});
    // Los usuarios son opcionales: un ENCARGADO no puede listarlos y el
    // filtro por persona simplemente no se le ofrece.
    api.get('/usuarios').then((u) => vigente && setUsuarios(u)).catch(() => setUsuarios([]));
    return () => { vigente = false; };
  }, []);

  useEffect(() => {
    let vigente = true;
    const t = setTimeout(() => {
      setCargando(true);
      api.getConTotal(
        `/auditoria?${consultaLista(filtros, { limite: porPagina, desplazamiento })}`,
      )
        .then((r) => { if (vigente) { setRegistros(r.filas); setTotal(r.total); } })
        .catch((e) => vigente && setError(e.message))
        .finally(() => vigente && setCargando(false));
    }, 250);
    return () => { vigente = false; clearTimeout(t); };
  }, [filtros, porPagina, desplazamiento]);

  // Recargar la lista tras un fallo, desde el botón «Volver a intentarlo».
  // Repite la misma consulta que el efecto de arriba; el efecto conserva
  // su guardia contra respuestas que llegan tarde.
  const recargar = useCallback(() => {
    setCargando(true);
    setError(null);
    return api.getConTotal(
      `/auditoria?${consultaLista(filtros, { limite: porPagina, desplazamiento })}`,
    )
      .then((r) => { setRegistros(r.filas); setTotal(r.total); })
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [filtros, porPagina, desplazamiento]);

  // El auditor externo no pide «la pantalla»: pide el periodo entero, con
  // el antes y el después de cada cambio. Exportar la página sería
  // entregarle una muestra y llamarla bitácora.
  const exportarTodo = useCallback(
    (alAvanzar) => traerTodo(
      (limite, desde) => `/auditoria?${consultaLista(filtros, {
        limite, desplazamiento: desde,
      })}`,
      { alAvanzar },
    ),
    [filtros],
  );

  const columnas = [
    { etiqueta: 'Cuándo', tipo: 'fecha_hora', valor: (r) => r.fecha, render: (r) => <TiempoRelativo valor={r.fecha} /> },
    { etiqueta: 'Quién', valor: (r) => r.usuario || '—', clase: 'font-medium text-gray-900' },
    // EN PALABRAS (EXPERIENCIA E3): nada de códigos, tablas, ids ni IPs en la
    // lista. Lo técnico sigue existiendo, en el detalle y en la exportación.
    { etiqueta: 'Qué hizo', valor: (r) => describirAccion(r.accion), clase: 'text-gray-900' },
    { etiqueta: 'Sobre qué', valor: (r) => describirSobreQue(r), clase: 'text-gray-600' },
    { etiqueta: 'Desde', valor: (r) => describirDesde(r.ip_origen), clase: 'text-gray-600' },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Auditoría"
        subtitulo="Toda escritura del sistema, con quién, cuándo y desde dónde."
        nota={
          <NotaExplicativa titulo="Por qué esta lista no se puede editar ni borrar">
            La bitácora es de <strong>solo inserción</strong>, impuesto por la base de datos: la cuenta con
            la que corre la aplicación no tiene permiso de modificarla ni de borrarla, y un disparador lo
            bloquea incluso para el administrador. Ni siquiera desde aquí se podría alterar un registro —
            que es justo lo que la hace valer algo ante un auditor. Exporta a CSV para entregarla.
          </NotaExplicativa>
        }
        filtros={
          <>
            {/* Ya no hace falta acordarse de volver a la página 1 en cada
                filtro: lo hace `useListaUrl`. Aquí estaba repetido cinco
                veces, y bastaba olvidarlo en uno para que filtrar desde la
                página 4 dejara la tabla vacía sin explicación. */}
            <Buscador valor={filtros.buscar} onChange={(v) => fijarFiltro({ buscar: v })} placeholder="SKU, folio, texto del cambio…" />
            <SelectFiltro
              valor={filtros.accion}
              onChange={(v) => fijarFiltro({ accion: v })}
              vacio="Todas las acciones"
              opciones={acciones.map((a) => ({ valor: a.accion, etiqueta: `${describirAccion(a.accion)} (${a.veces})` }))}
            />
            {usuarios.length > 0 && (
              <SelectFiltro
                valor={filtros.id_usuario}
                onChange={(v) => fijarFiltro({ id_usuario: v })}
                vacio="Cualquier persona"
                opciones={usuarios.map((u) => ({ valor: String(u.id), etiqueta: u.nombre_completo }))}
              />
            )}
            <RangoFechas
              desde={filtros.desde}
              hasta={filtros.hasta}
              onDesde={(v) => fijarFiltro({ desde: v })}
              onHasta={(v) => fijarFiltro({ hasta: v })}
            />
          </>
        }
        columnas={columnas}
        filas={registros}
        cargando={cargando}
        error={error}
        onReintentar={recargar}
        exportar={{ nombre: 'auditoria', columnas: COLS_EXPORT, obtenerTodo: exportarTodo }}
        paginador={
          <Paginador
            pagina={pagina}
            porPagina={porPagina}
            total={total}
            mostrados={registros.length}
            cargando={cargando}
            onPagina={fijarPagina}
          />
        }
        claveFila={(r) => r.id}
        vacio={
          <EstadoVacio
            icono={FileSearch}
            titulo="Nada coincide con esos filtros"
            descripcion="Prueba a ampliar el rango de fechas o a quitar el filtro de acción."
          />
        }
        acciones={(r) => [{ etiqueta: 'Ver cambio', onClick: () => setDetalle(r) }]}
      />

      {/* El fallo de carga lo pinta la lista (motivo en español + reintentar). */}

      {detalle && <ModalDetalle r={detalle} onCerrar={() => setDetalle(null)} />}
    </>
  );
}

function ModalDetalle({ r, onCerrar }) {
  // R-EX1 (decisión del dueño, 17 sep 2026): lo técnico se queda detrás del
  // clic, y solo lo ve quien administra.
  const { tieneNivel } = useAuth();
  const administra = tieneNivel('ADMIN');
  const campos = new Set([
    ...Object.keys(r.datos_antes || {}),
    ...Object.keys(r.datos_despues || {}),
  ]);

  return (
    <Modal
      titulo={describirAccion(r.accion)}
      subtitulo={`${r.usuario || '—'} · ${describirSobreQue(r)}`}
      ancho="max-w-lg"
      onCerrar={onCerrar}
      pie={
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button type="button" onClick={onCerrar} className="text-sm font-semibold px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
            Cerrar
          </button>
        </div>
      }
    >
        <div className="space-y-3">
          {campos.size === 0 && (
            <p className="text-sm text-gray-500">
              Esta acción no registró un antes y un después: solo consta que ocurrió.
            </p>
          )}
          {campos.size > 0 && (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 text-sm">
              {[...campos].flatMap((c) => {
                // CAMPO A CAMPO, también lo anidado: un valor que era un
                // objeto se abre en sus partes; nunca se pinta como JSON.
                const antes = Object.fromEntries(aplanar(r.datos_antes?.[c], describirCampo(c)));
                const despues = Object.fromEntries(aplanar(r.datos_despues?.[c], describirCampo(c)));
                const claves = [...new Set([...Object.keys(antes), ...Object.keys(despues)])];
                return claves.map((k) => (
                  <div key={`${c}:${k}`} className="px-3 py-2">
                    <span className="block text-xs text-gray-600">{k}</span>
                    <span className="block">
                      {antes[k] !== undefined && antes[k] !== despues[k] && (
                        <>
                          <span className="text-gray-600 line-through">{antes[k]}</span>
                          <span className="mx-1.5 text-gray-600">→</span>
                        </>
                      )}
                      <span className="text-gray-900 font-medium">{despues[k] ?? '—'}</span>
                    </span>
                  </div>
                ));
              })}
            </div>
          )}

          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 text-sm">
            <Dato campo="Cuándo" valor={new Date(r.fecha).toLocaleString('es-MX')} />
            <Dato campo="Correo" valor={r.email} />
            <Dato campo="Desde" valor={describirDesde(r.ip_origen)} />
            <Dato campo="Dispositivo" valor={describirDispositivo(r.user_agent)} />
          </div>
          {/* LO TÉCNICO, para soporte o un auditor, detrás de un clic y dicho
              como tal: la referencia exacta del registro, la IP y el navegador
              completo. No es lo primero que ve nadie. */}
          {administra && <details className="text-xs text-gray-600">
            <summary className="cursor-pointer select-none">Datos técnicos (para soporte)</summary>
            <div className="mt-1 border border-gray-200 rounded-lg divide-y divide-gray-100">
              <Dato campo="Acción" valor={r.accion} />
              <Dato campo="Registro" valor={`${r.entidad}${r.id_entidad ? ` · ${r.id_entidad}` : ''}`} />
              <Dato campo="IP" valor={r.ip_origen} />
              <Dato campo="Navegador" valor={r.user_agent} />
            </div>
          </details>}
        </div>
    </Modal>
  );
}

function Dato({ campo, valor }) {
  return (
    <div className="flex items-start justify-between gap-3 px-3 py-2">
      <span className="text-gray-500 shrink-0">{campo}</span>
      <span className="text-gray-900 text-right break-all">{valor || '—'}</span>
    </div>
  );
}

// `fmt` se quedó sin uso en E3: lo anidado se abre con `aplanar`, campo a
// campo, y ya no hay `JSON.stringify` camino a la pantalla. Solo la
// exportación (COLS_EXPORT) sigue llevando el JSON entero, que es lo que
// un auditor pide.
